<template>
  <div class="FilterWrapperMobile">
    <div
      ref="mobileFiltersRef"
      class="mobile-filters flex flex-nowrap col-1-1 no-p"
    >
      <UIIconButton
        label="Toon Filters"
        class="btn-primary"
        custom-icon="filter"
        @click="openDetailFilter('')"
      />

      <button
        v-if="hasDateFilter"
        class="btn btn-secondary mobile-filter m-0"
        :class="{ 'active-filter': initialDateValue && initialDateValue[1] }"
        @click="openDetailFilter('datum')"
      >
        <span class="text">Datum</span>
        <UIIconComponent icon="chevron-down" />
      </button>

      <button
        v-for="filter in availableFilters"
        :key="filter.value"
        class="btn btn-secondary mobile-filter m-0"
        :class="{ 'active-filter': hasActiveFilters(filter) }"
        @click="openDetailFilter(filter)"
      >
        <span class="text">
          {{ filter.label }}
        </span>
        <UIIconComponent icon="chevron-down" />
        <span
          v-if="hasActiveFilters(filter)"
          class="count"
        >
          {{ filter.options.filter((option) => option.isSelected).length }}
        </span>
      </button>
    </div>

    <div
      v-if="hasOverflow"
      class="scroll-progress-track"
    >
      <div
        class="scroll-progress-fill"
        :style="{ width: `${thumbWidth}%` }"
      />
    </div>

    <!-- TOTAL FILTERS -->
    <UIModal
      :is-open="showFilters && showDetailFilter === ''"
      title="Filters"
      :primary-label="`Bekijk ${totalResults} resultaten`"
      :secondary-label="'Filters wissen'"
      extra-class="adaptive-colors reverse"
      @close="closeDetailFilter"
      @primary="closeDetailFilter"
      @secondary="emit('clear-all-filters')"
    >
      <template #body>
        <UIEventsFilterDate
          v-if="hasDateFilter"
          class="filter-item mb-md"
          :initial-date-value="initialDateValue"
          @update-date="(startDate, endDate) => emit('update-date', startDate, endDate)"
        />
        <UIModuleFilterListItems
          v-for="filter in availableFilters"
          :key="filter.value"
          class="filter-item mb-md"
          :filter="filter"
          :show-delete="false"
          :max-items="5"
          @update-filter="(filter, option) => emit('update-filter', filter, option)"
          @delete-filter="(filter) => emit('delete-filter', filter)"
        />
      </template>
    </UIModal>

    <!-- SINGLE FILTERS -->
    <UIModal
      v-for="(filterOption, idx) in availableFilters"
      :key="idx"
      :is-open="checkFilterOpen(filterOption)"
      :title="filterOption.label"
      :primary-label="`Bekijk ${totalResults} resultaten`"
      :secondary-label="'Filter wissen'"
      @close="closeDetailFilter"
      @primary="closeDetailFilter"
      @secondary="emit('delete-filter', filterOption)"
    >
      <template #body>
        <UIModuleFilterListItems
          :filter="filterOption"
          :show-delete="false"
          :show-label="false"
          @update-filter="(filter, option) => emit('update-filter', filter, option)"
          @delete-filter="(filter) => emit('delete-filter', filter)"
        />
      </template>
    </UIModal>

    <!-- DATE FILTER -->
    <UIModal
      v-if="hasDateFilter"
      :is-open="showFilters && showDetailFilter === 'datum'"
      title="Datum"
      :primary-label="`Bekijk ${totalResults} resultaten`"
      :secondary-label="'Filter wissen'"
      @close="closeDetailFilter"
      @primary="closeDetailFilter"
      @secondary="emit('update-date', null, null)"
    >
      <template #body>
        <UIEventsFilterDate
          :initial-date-value="initialDateValue"
          :modal-always-open="true"
          @update-date="(startDate, endDate) => emit('update-date', startDate, endDate)"
        />
      </template>
    </UIModal>
  </div>
</template>
<script setup lang="ts">
  import { useFilterHelpers } from '#imports'
  import type { FilterOption } from '@tc8l/nuxt-overview'

  defineProps<{
    availableFilters: FilterOption[] | undefined
    hasDateFilter?: boolean
    initialDateValue?: [Date, Date]
    totalResults?: number
  }>()

  const showFilters = ref(false)
  const showDetailFilter = ref<any | undefined>(undefined)
  const localDetailFilter = ref<any | undefined>(undefined)

  const mobileFiltersRef = ref<HTMLElement | null>(null)
  const thumbWidth = ref(100)
  const hasOverflow = ref(false)
  let resizeObserver: ResizeObserver | null = null

  function updateScrollProgress() {
    const el = mobileFiltersRef.value
    if (!el) return

    const scrollable = el.scrollWidth - el.clientWidth
    hasOverflow.value = scrollable > 0

    if (!hasOverflow.value) {
      thumbWidth.value = 100
      return
    }

    const minWidth = (el.clientWidth / el.scrollWidth) * 100
    const progress = el.scrollLeft / scrollable

    thumbWidth.value = minWidth + progress * (100 - minWidth)
  }

  onMounted(() => {
    if (!mobileFiltersRef.value) return

    mobileFiltersRef.value.addEventListener('scroll', updateScrollProgress, { passive: true })

    resizeObserver = new ResizeObserver(updateScrollProgress)
    resizeObserver.observe(mobileFiltersRef.value)
  })

  onUnmounted(() => {
    mobileFiltersRef.value?.removeEventListener('scroll', updateScrollProgress)
    resizeObserver?.disconnect()
  })

  function openDetailFilter(filterValue: any) {
    if (typeof filterValue === 'string') {
      showDetailFilter.value = filterValue
    } else {
      showDetailFilter.value = filterValue.value + '-' + filterValue.label
      localDetailFilter.value = filterValue
    }
    showFilters.value = true
    document.documentElement.classList.add('overlay-open')
  }

  function checkFilterOpen(filter: any) {
    return showFilters.value && showDetailFilter.value === filter.value + '-' + filter.label
  }

  function closeDetailFilter() {
    showDetailFilter.value = undefined
    localDetailFilter.value = undefined
    showFilters.value = false
    document.documentElement.classList.remove('overlay-open')
  }

  const { hasActiveFilters } = useFilterHelpers()

  const emit = defineEmits<{
    (e: 'update-filter', filterValue: any, optionLabel: any): void
    (e: 'delete-filter', filter: FilterOption): void
    (e: 'clear-all-filters'): void
    (e: 'update-date', startDate: Date | null, endDate: Date | null): void
  }>()
</script>
<style scoped lang="scss">
  .FilterWrapperMobile {
    display: flex;
    flex-direction: column;
    width: 100vw;
    margin-left: calc((var(--grid-gutter) + var(--gutter)) * -1);
  }

  .mobile-filters {
    overflow-x: scroll;
    overflow-y: visible;
    padding: 0.8rem calc((var(--grid-gutter) + var(--gutter)));
    gap: var(--margin-sm);
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .scroll-progress-track {
    position: relative;
    height: 0.3rem;
    margin: 0 calc((var(--grid-gutter) + var(--gutter))) 0.8rem;
    border-radius: 999px;
    background-color: rgba($color-highlight, 0.2);
    overflow: hidden;
    margin-top: var(--margin-xlg);
  }

  .scroll-progress-fill {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    border-radius: inherit;
    background-color: $color-highlight;
    transition: width 0.1s ease-out;
  }

  .mobile-filter {
    position: relative;
    white-space: nowrap;

    .count {
      position: absolute;
      top: 0;
      right: 0;
      width: 3.2rem;
      font-size: 1.6rem;
      height: 3.2rem;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #000;
      color: #fff;
      z-index: 5;
      transform: translateX(25%) translateY(-25%);
    }
  }
</style>
